// AssignGuards.jsx
import React, { useState, useEffect, useContext } from 'react';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase';
import { User, UserPlus, UserMinus, Search, Check } from 'lucide-react';
import { AuthContext } from "../../context/AuthContext";
import { fetchVigilantesPorServicio } from "../admin/firestoreService";
import './styles/AssignGuards.scss';

const AssignGuards = ({ routeId, assignedGuards = [], onUpdate }) => {
  const [allGuards, setAllGuards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuard, setSelectedGuard] = useState(null);

  const { currentUser, currentService, adminData } = useContext(AuthContext);

  useEffect(() => {
    if (currentService && currentService.id) {
      fetchAllGuards();
    }
  }, [currentService]);

  const fetchAllGuards = async () => {
    try {
      setLoading(true);
      // Usar la función de firestoreService para obtener vigilantes del servicio
      const guardsList = await fetchVigilantesPorServicio(currentService);
      
      // Marcar los vigilantes que ya están asignados a esta ruta
      const guardsWithAssignment = guardsList.map(guard => ({
        ...guard,
        isAssigned: assignedGuards.some(g => g.id === guard.id)
      }));
      
      setAllGuards(guardsWithAssignment);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching guards:', err);
      setError('Error al cargar la lista de usuarios');
      setLoading(false);
    }
  };

  const handleAssignGuard = async (guard) => {
    try {
      setLoading(true);
      
      // Actualizar el documento del vigilante en la subcolección correcta
      const guardRef = doc(db, 'servicios', currentService.id, 'vigilantes', guard.id);
      await updateDoc(guardRef, {
        rutasAsignadas: arrayUnion(routeId) // Cambio de 'assignedRoutes' a 'rutasAsignadas'
      });
      
      // Actualizar la interfaz
      setAllGuards(allGuards.map(g => 
        g.id === guard.id ? { ...g, isAssigned: true } : g
      ));
      
      // Notificar al componente padre para que actualice la lista
      onUpdate();
      setLoading(false);
    } catch (err) {
      console.error('Error assigning guard:', err);
      setError('Error al asignar el usuario');
      setLoading(false);
    }
  };

  const handleRemoveGuard = async (guard) => {
    try {
      setLoading(true);
      
      // Actualizar el documento del vigilante en la subcolección correcta
      const guardRef = doc(db, 'servicios', currentService.id, 'vigilantes', guard.id);
      await updateDoc(guardRef, {
        rutasAsignadas: arrayRemove(routeId) // Cambio de 'assignedRoutes' a 'rutasAsignadas'
      });
      
      // Actualizar la interfaz
      setAllGuards(allGuards.map(g => 
        g.id === guard.id ? { ...g, isAssigned: false } : g
      ));
      
      // Notificar al componente padre para que actualice la lista
      onUpdate();
      setLoading(false);
    } catch (err) {
      console.error('Error removing guard:', err);
      setError('Error al remover el usuario');
      setLoading(false);
    }
  };

  const filteredGuards = allGuards.filter(guard => {
    const searchLower = searchTerm.toLowerCase();
    const guardName = guard.name || guard.displayName || '';
    const guardEmail = guard.email || '';
    
    return (
      guardName.toLowerCase().includes(searchLower) ||
      guardEmail.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="assign-guards">
      <div className="header">
        <h3 className="title">Usuarios Asignados</h3>
        {loading && <span className="loading-text">Cargando...</span>}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="guards-container">
        <div className="guards-column">
          <div className="search-container">
            <div className="search-input-container">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="guards-list">
            {filteredGuards.length === 0 ? (
              <p className="empty-message">No se encontraron usuarios</p>
            ) : (
              <ul className="guards-items">
                {filteredGuards.map(guard => (
                  <li 
                    key={guard.id}
                    className={`guard-item ${selectedGuard?.id === guard.id ? 'selected' : ''}`}
                    onClick={() => setSelectedGuard(guard)}
                  >
                    <div className="guard-info">
                      <User size={20} className="guard-icon" />
                      <div className="guard-details">
                        <div className="guard-name">{guard.displayName || guard.name || 'Sin nombre'}</div>
                        <div className="guard-email">{guard.email || 'Sin email'}</div>
                      </div>
                    </div>
                    
                    {guard.isAssigned ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveGuard(guard);
                        }}
                        className="btn btn-remove"
                        disabled={loading}
                      >
                        <UserMinus size={14} className="btn-icon" />
                        Eliminar
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssignGuard(guard);
                        }}
                        className="btn btn-assign"
                        disabled={loading}
                      >
                        <UserPlus size={14} className="btn-icon" />
                        Asignar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        <div className="guards-column">
          <div className="assigned-header">
            <h4 className="assigned-title">Usuarios Asignados</h4>
          </div>
          
          <div className="assigned-list">
            {assignedGuards.length === 0 ? (
              <p className="empty-message">No hay usuarios asignados a esta ruta</p>
            ) : (
              <ul className="assigned-items">
                {assignedGuards.map(guard => (
                  <li 
                    key={guard.id}
                    className="assigned-item"
                  >
                    <div className="guard-info">
                      <User size={20} className="guard-icon" />
                      <div className="guard-details">
                        <div className="guard-name">{guard.displayName || guard.name || 'Sin nombre'}</div>
                        <div className="guard-email">{guard.email || 'Sin email'}</div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleRemoveGuard(guard)}
                      className="btn-icon-only"
                      disabled={loading}
                    >
                      <UserMinus size={18} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      
      {selectedGuard && !selectedGuard.isAssigned && (
        <div className="selected-action">
          <button
            onClick={() => handleAssignGuard(selectedGuard)}
            className="btn btn-primary"
            disabled={loading}
          >
            <Check size={18} className="btn-icon" />
            Asignar Usuario Seleccionado
          </button>
        </div>
      )}
    </div>
  );
};

export default AssignGuards;